/** Breathing room between the sticky header's bottom edge and the card. */
const HEADER_GAP = 12;

/**
 * Nearest ancestor that actually scrolls. The feed's scroll box is the layout's
 * `#outlet-container`, but only from `lg` up (`overflow-y-visible … lg:overflow-auto`) —
 * below that the window scrolls the page. Resolving it by behaviour instead of by id keeps
 * both cases (and any future layout change) working.
 */
const findScrollParent = (element: HTMLElement): HTMLElement | null => {
	let node = element.parentElement;

	while (node) {
		const { overflowY } = window.getComputedStyle(node);

		if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
			return node;
		}

		node = node.parentElement;
	}

	return null;
};

/**
 * Scrolls `card` to the top of the feed's scroll area, offset so it clears the sticky header
 * (topbar + tabs + filter bar) instead of hiding underneath it. The header has no fixed height —
 * it grows when the filter bar wraps — so its height is measured, never assumed.
 *
 * Deliberately not `Element.scrollIntoView`: that scrolls EVERY scrollable ancestor, including
 * `overflow-hidden` layout boxes, which have no scrollbar to undo the nudge — see
 * `scrollChatToTarget.util.ts` for the same lesson. Scrolling the resolved container directly
 * leaves every other box alone.
 */
export const scrollFeedCardIntoView = (
	card: HTMLElement | null,
	stickyHeader: HTMLElement | null
): void => {
	if (!card) {
		return;
	}

	const offset = (stickyHeader?.getBoundingClientRect().height ?? 0) + HEADER_GAP;
	const scroller = findScrollParent(card);

	// jsdom implements neither — a specs-only guard, harmless in the browser.
	if (scroller && typeof scroller.scrollTo === "function") {
		const top =
			card.getBoundingClientRect().top -
			scroller.getBoundingClientRect().top +
			scroller.scrollTop -
			offset;

		scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

		return;
	}

	if (!scroller && typeof window.scrollTo === "function") {
		const top = card.getBoundingClientRect().top + window.scrollY - offset;

		window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
	}
};
